/**
 * Simple auth check that redirects authenticated users to the customer app
 * Checks localStorage for access_token since both apps share the same domain
 */

import mixpanel from 'mixpanel-browser';
import {
  ACCESS_TOKEN,
  LOGIN_PATH,
} from '../constants/authConstants';
import {getBrandFromEnv, getPlatform} from '../mixpanel/mixpanel';

const AUTHENTICATED_USER_REDIRECT_URL = process.env.GATSBY_AUTHENTICATED_USER_REDIRECT_URL;
const ENABLE_AUTH_REDIRECT_FEATURE = process.env.GATSBY_ENABLE_AUTH_REDIRECT;

const brand = getBrandFromEnv();
const isMFBBrand = brand === 'MFB';

const MixpanelAutoLoginAttemptEvent = 'Website - Auto Login Attempted';
const MixPanelAutoLoginSuccessEvent = 'Website - Auto Login Success';

/**
 * Decode JWT token and extract CustomerNumber claim
 * @param token JWT token string
 * @returns Customer number or null if not found
 */
const extractCustomerNumberFromJWT = (token: string): string | null => {
  try {
    // JWT tokens are in format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(decodedPayload);

    // Extract CustomerNumber claim
    return claims.CustomerNumber || null;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

export const checkAuthAndRedirect = () => {
  try {
    // Only run in browser
    if (globalThis.window === undefined) {
      return;
    }

    let customerNumber;

    const accessToken = localStorage.getItem(ACCESS_TOKEN);

    if (accessToken) {
      // Extract and store customer number from JWT token
      customerNumber = extractCustomerNumberFromJWT(accessToken);
      if (customerNumber) {
        mixpanel.identify(customerNumber);
      }
    }

    // Feature flag
    if (!ENABLE_AUTH_REDIRECT_FEATURE) {
      return;
    }

    const isHomePage =
      globalThis.window.location.pathname === '/' ||
      globalThis.window.location.pathname === '';

    const isLoginPage = globalThis.window.location.pathname === LOGIN_PATH;

    if (isHomePage && customerNumber && isMFBBrand) {
      mixpanel.track(MixpanelAutoLoginAttemptEvent, {
        'Platform': getPlatform(brand),
        'Customer Number': customerNumber,
      });
      return;
    }

    if (isLoginPage && customerNumber && isMFBBrand) {
      mixpanel.track(MixPanelAutoLoginSuccessEvent, {
        'Platform': getPlatform(brand),
        'Customer Number': customerNumber,
      });

      if (AUTHENTICATED_USER_REDIRECT_URL) {
        globalThis.window.location.href = AUTHENTICATED_USER_REDIRECT_URL;
        return;
      }
    }
  } catch (error) {
    // Fail silently
    console.error('Error checking auth:', error);
  }
};
